import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';
import MediaBox from '../components/MediaBox';
import { auth } from '../firebase';
import {
  adminDeleteContent,
  AdminContentItem,
  AdminContentType,
  deleteAdminMusicTrack,
  deleteReportContent,
  getAdminStats,
  getUserMe,
  listAdminAds,
  listAdminContent,
  listAdminArtistVerifications,
  listAdminMusicTracks,
  listAdminUsers,
  listAppeals,
  listReports,
  listWithdrawals,
  resolveAppeal,
  runAdminTool,
  updateAdminAd,
  updateAdminArtistVerification,
  updateAdminMusicTrack,
  updateReportStatus,
  updateWithdrawal,
} from '../utils/secureFunctions';

type AdminStats = {
  totalUsers?: number;
  activeUsersLast30Days?: number;
  newUsersToday?: number;
  newUsersThisWeek?: number;
  newUsersThisMonth?: number;
  totalPosts?: number;
  postsToday?: number;
  totalVideos?: number;
  totalReels?: number;
  totalTestimonies?: number;
  totalGroups?: number;
  totalAds?: number;
  activeAds?: number;
  totalAdRevenue?: number;
  platformRevenue?: number;
  creatorPayouts?: number;
  pendingVerifications?: number;
  pendingWithdrawals?: number;
  totalWithdrawn?: number;
  pendingReports?: number;
};

type AdminUser = {
  id: string;
  role: string;
  banned: boolean;
  banReason?: string | null;
  suspendedUntil?: string | Date | null;
  createdAt?: string | Date;
  profile?: {
    name?: string;
    username?: string | null;
    email?: string | null;
    photoURL?: string | null;
    verified?: boolean;
  } | null;
};

type ArtistVerification = {
  id: string;
  userId: string;
  fullName?: string;
  verificationType?: string;
  documents?: Array<{ type: string; url: string; fileName: string; viewUrl?: string | null }>;
  bankDetails?: { accountName?: string; accountNumber?: string; bankName?: string; country?: string };
  status?: string;
  rejectionReason?: string | null;
  createdAt?: string | Date;
  followers?: number;
  profile?: { name?: string; username?: string | null; photoURL?: string | null; verified?: boolean } | null;
};

type MusicTrack = {
  id: string;
  title: string;
  artistName: string;
  artistId: string;
  audioURL: string;
  isPublic: boolean;
  allowReels: boolean;
  status: string;
  createdAt?: string | Date;
};

type AdminAd = {
  id: string;
  title: string;
  description?: string;
  createdBy?: string;
  budget?: number;
  budgetSpent?: number;
  impressions?: number;
  clicks?: number;
  status?: string;
  reviewStatus?: string | null;
  createdAt?: string | Date;
};

type ReportItem = {
  id: string;
  contentId?: string;
  contentType?: string;
  reason?: string;
  status?: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  createdAt?: string | number | Date | { seconds: number };
  additionalInfo?: string | null;
  reporterName?: string | null;
  reportedUserId?: string;
  reportedUserName?: string | null;
  preview?: { text?: string; imageURL?: string | null; videoURL?: string | null; author?: string; link?: string } | null;
};

type WithdrawalItem = {
  id: string;
  userId?: string;
  amount?: number;
  currency?: string;
  method?: string;
  status?: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt?: string | number | Date | { seconds: number };
};

type AppealItem = {
  id: string;
  status?: string;
  decision?: string | null;
  reason?: string | null;
  contentType?: string;
  originalStatus?: string;
  originalReason?: string | null;
  createdAt?: string | number | Date | { seconds: number };
  userId?: string | null;
  userName?: string | null;
  preview?: { text?: string; imageURL?: string | null; videoURL?: string | null; author?: string; link?: string } | null;
};

const formatDate = (value: ReportItem['createdAt'] | WithdrawalItem['createdAt'] | AppealItem['createdAt']) => {
  if (!value) return '—';
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
  }
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).toLocaleString();
  }
  return '—';
};

const formatMoney = (amount?: number, currency = 'USD') => {
  const value = typeof amount === 'number' ? amount : 0;
  return `${currency} ${value.toLocaleString()}`;
};

const ROLE_LABELS: Record<string, string> = {
  user: 'User',
  moderator: 'Moderator (reviews reports)',
  admin: 'Admin (full dashboard)',
  super_admin: 'Super Admin (owner)',
};

const EnhancedAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'posts' | 'users' | 'ads' | 'verifications' | 'music' | 'reports' | 'withdrawals' | 'appeals'>(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab === 'appeals' || tab === 'reports' || tab === 'withdrawals' || tab === 'verifications' ? tab : 'overview';
  });

  const [stats, setStats] = useState<AdminStats>({});
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalItem[]>([]);
  const [appeals, setAppeals] = useState<AppealItem[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [ads, setAds] = useState<AdminAd[]>([]);
  const [adFilter, setAdFilter] = useState<'all' | 'pending_approval' | 'active' | 'paused' | 'rejected' | 'completed' | 'draft'>('pending_approval');
  const [verifications, setVerifications] = useState<ArtistVerification[]>([]);
  const [verificationFilter, setVerificationFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'additional_info_required'>('pending');
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [musicFilter, setMusicFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [contentType, setContentType] = useState<AdminContentType>('post');
  const [contentQuery, setContentQuery] = useState('');
  const [contentSearch, setContentSearch] = useState('');
  const [contentItems, setContentItems] = useState<AdminContentItem[]>([]);
  const [contentNext, setContentNext] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentNotice, setContentNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [busyContent, setBusyContent] = useState<string | null>(null);
  const [openContent, setOpenContent] = useState<string | null>(null);
  const [selectedContent, setSelectedContent] = useState<Set<string>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => { setSelectedContent(new Set()); }, [contentType, contentSearch]);

  const toggleSelected = (id: string) => {
    setSelectedContent((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const loadContent = useCallback(async (type: AdminContentType, q: string, before?: string) => {
    setContentLoading(true);
    try {
      const res = await listAdminContent({ type, q: q || undefined, before, limit: 30 });
      setContentItems((prev) => (before ? [...prev, ...res.items] : res.items));
      setContentNext(res.nextBefore);
    } catch (err: any) {
      setContentNotice({ kind: 'err', text: err?.message || 'Could not load content.' });
    } finally {
      setContentLoading(false);
    }
  }, []);

  const handleAdminDelete = async (item: AdminContentItem) => {
    const label = item.type === 'pulse' ? 'Pulse video' : item.type;
    const reason = window.prompt(`Delete this ${label} by ${item.author}?\n\nOptional: reason sent to the author (leave blank for the default message).`, '');
    if (reason === null) return;
    setBusyContent(item.id);
    try {
      await adminDeleteContent(item.type, item.id, reason.trim() || undefined);
      setContentItems((prev) => prev.filter((x) => x.id !== item.id));
      setStats((prev) => {
        const key = item.type === 'post' ? 'totalPosts' : item.type === 'pulse' ? 'totalVideos' : 'totalTestimonies';
        const cur = Number((prev as any)[key] || 0);
        return { ...prev, [key]: Math.max(0, cur - 1) };
      });
      setContentNotice({ kind: 'ok', text: `${label.charAt(0).toUpperCase() + label.slice(1)} by ${item.author} was deleted and the author has been notified.` });
    } catch (err: any) {
      setContentNotice({ kind: 'err', text: `Could not delete: ${err?.message || 'unknown error'}` });
    } finally {
      setBusyContent(null);
    }
  };

  const handleBulkDelete = async () => {
    const targets = contentItems.filter((x) => selectedContent.has(x.id));
    if (!targets.length || bulkProgress) return;
    const label = contentType === 'pulse' ? 'Pulse videos' : contentType === 'testimony' ? 'testimonies' : 'posts';
    if (!window.confirm(`Permanently delete ${targets.length} ${label}? This cannot be undone.`)) return;
    const reason = window.prompt('Optional: reason sent to each author (leave blank for the default message).', '');
    if (reason === null) return;
    const statKey = contentType === 'post' ? 'totalPosts' : contentType === 'pulse' ? 'totalVideos' : 'totalTestimonies';
    const deleted: string[] = [];
    let failed = 0;
    setBulkProgress({ done: 0, total: targets.length });
    for (const item of targets) {
      let ok = false;
      for (let attempt = 0; attempt < 3 && !ok; attempt++) {
        try {
          await adminDeleteContent(item.type, item.id, reason.trim() || undefined);
          ok = true;
        } catch (err: any) {
          const limited = err?.code === 429 || /too many|rate/i.test(String(err?.message || ''));
          if (!limited) break;
          await new Promise((r) => setTimeout(r, 20000));
        }
      }
      if (ok) deleted.push(item.id); else failed++;
      setBulkProgress({ done: deleted.length + failed, total: targets.length });
    }
    const gone = new Set(deleted);
    setContentItems((prev) => prev.filter((x) => !gone.has(x.id)));
    setSelectedContent(new Set());
    setStats((prev) => ({ ...prev, [statKey]: Math.max(0, Number((prev as any)[statKey] || 0) - deleted.length) }));
    setBulkProgress(null);
    setContentNotice(failed
      ? { kind: 'err', text: `Deleted ${deleted.length} of ${targets.length} ${label}; ${failed} could not be deleted. Authors of deleted items were notified.` }
      : { kind: 'ok', text: `Deleted ${deleted.length} ${label}. Each author has been notified.` });
  };

  const openPostsTab = (type: AdminContentType) => {
    setContentType(type);
    setActiveTab('posts');
  };

  const loadOverview = useCallback(async () => {
    const [statsRes, reportsRes, withdrawalsRes, appealsRes] = await Promise.allSettled([
      getAdminStats(),
      listReports('pending'),
      listWithdrawals('pending'),
      listAppeals('pending'),
    ]);

    if (statsRes.status === 'fulfilled') setStats(statsRes.value.stats || {});
    if (reportsRes.status === 'fulfilled') setReports((reportsRes.value.reports || []) as ReportItem[]);
    if (withdrawalsRes.status === 'fulfilled') setWithdrawals((withdrawalsRes.value.withdrawals || []) as WithdrawalItem[]);
    if (appealsRes.status === 'fulfilled') setAppeals((appealsRes.value.appeals || []) as AppealItem[]);

    const firstError = [statsRes, reportsRes, withdrawalsRes, appealsRes].find(r => r.status === 'rejected');
    if (firstError && firstError.status === 'rejected') {
      setError(firstError.reason?.message || 'Failed to load admin data.');
    }
  }, []);

  const loadUsers = useCallback(async (query?: string) => {
    const res = await listAdminUsers({ q: query || undefined, limit: 200 });
    setUsers((res.users || []) as AdminUser[]);
  }, []);

  const loadAds = useCallback(async (status?: string) => {
    const res = await listAdminAds({ status: status === 'all' ? undefined : (status as any), limit: 200 });
    setAds((res.ads || []) as AdminAd[]);
  }, []);

  const loadVerifications = useCallback(async (status?: string) => {
    const res = await listAdminArtistVerifications({ status: status === 'all' ? undefined : (status as any), limit: 200 });
    setVerifications((res.verifications || []) as ArtistVerification[]);
  }, []);

  const loadTracks = useCallback(async (status?: string) => {
    const res = await listAdminMusicTracks({ status: status === 'all' ? undefined : status, limit: 200 });
    setTracks((res.tracks || []) as MusicTrack[]);
  }, []);

  const [myRole, setMyRole] = useState<string>('');

  useEffect(() => {
    const checkAccess = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      try {
        const me = await getUserMe();
        const role = (me.profile as { role?: string } | undefined)?.role;
        if (role !== 'admin' && role !== 'super_admin' && role !== 'moderator') {
          navigate('/home');
          return;
        }
        setMyRole(role);
        await loadOverview();
      } catch (err) {
        console.error('Admin access check failed:', err);
        navigate('/home');
      } finally {
        setLoading(false);
      }
    };
    checkAccess();
  }, [loadOverview, navigate]);

  useEffect(() => {
    if (activeTab === 'posts') loadContent(contentType, contentSearch);
    if (activeTab === 'users') loadUsers(userQuery);
    if (activeTab === 'ads') loadAds(adFilter);
    if (activeTab === 'verifications') loadVerifications(verificationFilter);
    if (activeTab === 'music') loadTracks(musicFilter);
  }, [activeTab, adFilter, verificationFilter, musicFilter, userQuery, contentType, contentSearch, loadContent, loadUsers, loadAds, loadVerifications, loadTracks]);

  const revenue = useMemo(() => {
    const total = stats.totalAdRevenue || 0;
    const platform = stats.platformRevenue ?? total * 0.45;
    const creator = stats.creatorPayouts ?? total * 0.55;
    return { total, platform, creator };
  }, [stats.creatorPayouts, stats.platformRevenue, stats.totalAdRevenue]);

  const [reportNotice, setReportNotice] = useState('');
  const [busyReport, setBusyReport] = useState<string | null>(null);

  const handleReportStatus = async (id: string, status: 'reviewed' | 'resolved' | 'dismissed') => {
    setBusyReport(id);
    try {
      await updateReportStatus(id, status);
      setReports(prev => prev.filter(r => r.id !== id));
      setReportNotice(status === 'dismissed' ? 'Report dismissed — the content stays up.' : 'Report closed.');
    } catch (e: any) {
      alert('Could not update report: ' + (e?.message || 'unknown error'));
    } finally {
      setBusyReport(null);
    }
  };

  const handleDeleteReportContent = async (id: string) => {
    if (!window.confirm('Remove this content from Belivah? The owner will be notified. This cannot be undone.')) return;
    setBusyReport(id);
    try {
      await deleteReportContent(id);
      setReports(prev => prev.filter(r => r.id !== id));
      setReportNotice('Content removed and the owner was notified.');
    } catch (e: any) {
      alert('Could not remove content: ' + (e?.message || 'unknown error'));
    } finally {
      setBusyReport(null);
    }
  };

  const handleWithdrawalStatus = async (id: string, status: 'processing' | 'completed' | 'failed') => {
    const failureReason = status === 'failed' ? window.prompt('Failure reason (optional):') || null : null;
    await updateWithdrawal(id, { status, failureReason });
    setWithdrawals(prev => prev.filter(w => w.id !== id));
  };

  const handleAppealResolve = async (id: string, decision: 'approved' | 'rejected') => {
    const note = window.prompt('Moderator note (optional):');
    if (note === null) return;
    try {
      await resolveAppeal(id, { decision, moderatorNote: note.trim() || null });
      setAppeals(prev => prev.filter(a => a.id !== id));
    } catch (e: any) {
      alert(e?.message || 'Could not resolve this appeal.');
    }
  };

  const handleUserRole = async (userId: string, role: string) => {
    if (!window.confirm(`Change this user's role to ${ROLE_LABELS[role] || role}?`)) return;
    try {
      await runAdminTool({ action: 'SET_ROLE', params: { userId, role } });
    } catch (e: any) {
      alert(e?.message || 'Could not change role');
    }
    await loadUsers(userQuery);
  };

  const handleUserDelete = async (userId: string, name: string) => {
    if (!window.confirm(`Permanently delete ${name || 'this user'} and all their posts, videos and data? This cannot be undone. Their deleted account will no longer appear in this list.`)) return;
    try {
      await runAdminTool({ action: 'DELETE_USER', params: { userId } });
      alert('User and associated data deleted.');
    } catch (e: any) {
      alert(e?.message || 'Could not delete user');
      return;
    }
    await loadUsers(userQuery);
  };

  const handleUserBan = async (userId: string, banned: boolean) => {
    try {
      if (banned) {
        const reason = window.prompt('Optional reason for banning this user:', 'admin_action');
        if (reason === null) return;
        await runAdminTool({ action: 'BAN_USER', params: { userId, reason } });
      } else {
        await runAdminTool({ action: 'UNBAN_USER', params: { userId } });
      }
    } catch (e: any) {
      alert(e?.message || 'Could not update user status');
      return;
    }
    await loadUsers(userQuery);
  };

  const handleUserSuspend = async (userId: string) => {
    const rawDays = window.prompt('Suspend for how many days? (1–365)', '7');
    if (rawDays === null) return;
    const days = Number(rawDays);
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      alert('Enter a whole number from 1 to 365.');
      return;
    }
    const reason = window.prompt('Optional reason for suspension:', 'admin_action');
    if (reason === null) return;
    try {
      await runAdminTool({ action: 'SUSPEND_USER', params: { userId, days, reason } });
      alert(`User suspended for ${days} day${days === 1 ? '' : 's'}.`);
    } catch (e: any) {
      alert(e?.message || 'Could not suspend user');
      return;
    }
    await loadUsers(userQuery);
  };

  const handleAdUpdate = async (id: string, status: string, reviewStatus?: string | null) => {
    await updateAdminAd(id, { status: status as any, reviewStatus: reviewStatus ?? null });
    await loadAds(adFilter);
  };

  const handleVerificationUpdate = async (id: string, status: ArtistVerification['status'], rejectionReason?: string | null) => {
    await updateAdminArtistVerification(id, { status: status as any, rejectionReason: rejectionReason ?? null });
    await loadVerifications(verificationFilter);
  };

  const handleTrackUpdate = async (id: string, payload: { status?: string; isPublic?: boolean; allowReels?: boolean }) => {
    await updateAdminMusicTrack(id, payload);
    await loadTracks(musicFilter);
  };

  const handleTrackDelete = async (id: string) => {
    if (!window.confirm('Delete this track permanently?')) return;
    await deleteAdminMusicTrack(id);
    await loadTracks(musicFilter);
  };

  if (loading) {
    return <div style={styles.loading}>Loading Studio...</div>;
  }

  return (
    <div style={styles.container}>
      <TopNavigation />
      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Studio</h1>
          <p style={styles.subtitle}>VPS Admin Console</p>
          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.tabs}>
          {(['overview', 'posts', 'users', 'ads', 'verifications', 'music', 'reports', 'withdrawals', 'appeals'] as const).map(tab => (
            <button
              key={tab}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.activeTab : {}) }}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
          <button style={styles.refreshBtn} onClick={loadOverview}>Refresh</button>
        </div>

        {activeTab === 'overview' && (
          <div style={styles.tabContent}>
            <h2 style={styles.sectionTitle}>Platform Overview</h2>
            <div style={styles.section}>
              <h3>User Statistics</h3>
              <div style={styles.statsGrid}>
                <StatCard label="Total Users" value={stats.totalUsers ?? 0} />
                <StatCard label="Active (30d)" value={stats.activeUsersLast30Days ?? 0} />
                <StatCard label="New Today" value={stats.newUsersToday ?? 0} />
                <StatCard label="New This Week" value={stats.newUsersThisWeek ?? 0} />
                <StatCard label="New This Month" value={stats.newUsersThisMonth ?? 0} />
              </div>
            </div>
            <div style={styles.section}>
              <h3>Content Statistics</h3>
              <div style={styles.statsGrid}>
                <StatCard label="Total Posts" value={stats.totalPosts ?? 0} subLabel={stats.postsToday ? `${stats.postsToday} today` : 'View & manage →'} onClick={() => openPostsTab('post')} />
                <StatCard label="Pulse Videos" value={stats.totalVideos ?? 0} subLabel="View & manage →" onClick={() => openPostsTab('pulse')} />
                <StatCard label="Reels" value={stats.totalReels ?? 0} />
                <StatCard label="Testimonies" value={stats.totalTestimonies ?? 0} subLabel="View & manage →" onClick={() => openPostsTab('testimony')} />
                <StatCard label="Groups" value={stats.totalGroups ?? 0} />
                <StatCard label="Pending Reports" value={stats.pendingReports ?? reports.length} onClick={() => setActiveTab('reports')} />
              </div>
            </div>
            <div style={styles.section}>
              <h3>Revenue Overview</h3>
              <div style={styles.statsGrid}>
                <StatCard label="Total Ad Revenue" value={revenue.total} prefix="$" />
                <StatCard label="Platform Revenue (45%+)" value={revenue.platform} prefix="$" />
                <StatCard label="Creator Earnings (55%)" value={revenue.creator} prefix="$" />
                <StatCard label="Total Withdrawn" value={stats.totalWithdrawn ?? 0} prefix="$" />
              </div>
            </div>
            <div style={styles.section}>
              <h3>Pending Actions</h3>
              <div style={styles.statsGrid}>
                <StatCard label="Verifications" value={stats.pendingVerifications ?? verifications.length} />
                <StatCard label="Withdrawals" value={stats.pendingWithdrawals ?? withdrawals.length} />
                <StatCard label="Appeals" value={appeals.length} onClick={() => setActiveTab('appeals')} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'posts' && (
          <div style={styles.tabContent}>
            <h2 style={styles.sectionTitle}>All Content</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
              {([['post', 'Posts'], ['pulse', 'Pulse videos'], ['testimony', 'Testimonies']] as const).map(([t, label]) => (
                <button key={t} onClick={() => setContentType(t)} style={{ ...styles.smallBtn, ...(contentType === t ? { background: '#072B2C', color: '#fff' } : {}) }}>{label}</button>
              ))}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); setContentSearch(contentQuery.trim()); }}
              style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}
            >
              <input
                value={contentQuery}
                onChange={(e) => setContentQuery(e.target.value)}
                placeholder="Search by text or author name..."
                style={{ flex: '1 1 220px', minWidth: 0, padding: '10px 12px', borderRadius: 8, border: '1px solid #ccc', fontSize: 14, boxSizing: 'border-box' }}
              />
              <button type="submit" style={styles.smallBtn}>Search</button>
              {contentSearch && <button type="button" style={styles.smallBtn} onClick={() => { setContentQuery(''); setContentSearch(''); }}>Clear</button>}
            </form>
            {contentNotice && (
              <div style={{ background: contentNotice.kind === 'ok' ? '#E8F7F2' : '#FDECEA', color: contentNotice.kind === 'ok' ? '#0B4F51' : '#B71C1C', padding: '10px 14px', borderRadius: 8, marginBottom: 14, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>{contentNotice.kind === 'ok' ? '✓ ' : '⚠️ '}{contentNotice.text}</span>
                <button onClick={() => setContentNotice(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
              </div>
            )}
            {contentItems.length > 0 && (
              <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 12px', marginBottom: 12, background: '#F5F7F7', border: '1px solid #E0E0E0', borderRadius: 10 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={contentItems.every((x) => selectedContent.has(x.id))}
                    onChange={(e) => setSelectedContent(e.target.checked ? new Set(contentItems.map((x) => x.id)) : new Set())}
                  />
                  Select all shown ({contentItems.length})
                </label>
                <span style={{ fontSize: 13, color: '#555' }}>{selectedContent.size} selected</span>
                <button
                  disabled={!selectedContent.size || !!bulkProgress}
                  style={{ ...styles.rejectBtn, marginLeft: 'auto', opacity: !selectedContent.size || bulkProgress ? 0.5 : 1 }}
                  onClick={handleBulkDelete}
                >
                  {bulkProgress ? `Deleting ${bulkProgress.done}/${bulkProgress.total}…` : `🗑️ Delete selected (${selectedContent.size})`}
                </button>
              </div>
            )}
            {!contentLoading && contentItems.length === 0 && <p style={{ color: '#666' }}>Nothing found.</p>}
            <div style={{ display: 'grid', gap: 12 }}>
              {contentItems.map((item) => {
                const expanded = openContent === item.id;
                const busy = busyContent === item.id;
                return (
                  <div key={item.id} style={{ border: '1px solid #E0E0E0', borderRadius: 12, padding: 14, background: '#fff', opacity: busy ? 0.6 : 1 }}>
                    <div
                      onClick={() => setOpenContent(expanded ? null : item.id)}
                      style={{ cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start' }}
                      title="Click to view"
                    >
                      <input
                        type="checkbox"
                        aria-label="Select for bulk delete"
                        checked={selectedContent.has(item.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelected(item.id)}
                        style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
                      />
                      {!expanded && (item.imageURL || item.videoURL) && (
                        <div style={{ width: 64, height: 64, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22 }}>
                          {item.imageURL ? <MediaBox src={item.imageURL} type="image" maxHeight={64} /> : '▶'}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                          <strong>{item.author || 'Unknown'}</strong> · {formatDate(item.createdAt)} · ❤️ {item.likeCount} · 💬 {item.commentCount}
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...(expanded ? {} : { maxHeight: 44, overflow: 'hidden' }) }}>
                          {item.text || <em style={{ color: '#999' }}>(no text)</em>}
                        </div>
                        {!expanded && <div style={{ fontSize: 12, color: '#0D47A1', marginTop: 4 }}>Click to view</div>}
                      </div>
                    </div>
                    {expanded && (item.videoURL || item.imageURL) && (
                      <div style={{ maxWidth: 320, marginTop: 10 }}>
                        {item.videoURL
                          ? <MediaBox src={item.videoURL} poster={item.imageURL || undefined} type="video" controls contain maxHeight={420} />
                          : <MediaBox src={item.imageURL as string} type="image" contain maxHeight={420} />}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      <button style={styles.smallBtn} onClick={() => setOpenContent(expanded ? null : item.id)}>{expanded ? 'Hide' : '👁️ View'}</button>
                      <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ ...styles.smallBtn, background: '#E3F2FD', color: '#0D47A1', textDecoration: 'none' }}>Open on site ↗</a>
                      <button disabled={busy} style={styles.rejectBtn} onClick={() => handleAdminDelete(item)}>{busy ? 'Deleting…' : '🗑️ Delete'}</button>
                    </div>
                  </div>
                );
              })}
            </div>
            {contentLoading && <p style={{ color: '#666' }}>Loading…</p>}
            {!contentLoading && contentNext && (
              <button style={{ ...styles.smallBtn, marginTop: 14 }} onClick={() => loadContent(contentType, contentSearch, contentNext)}>Load more</button>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div style={styles.tabContent}>
            <h2 style={styles.sectionTitle}>User Management</h2>
            <div style={styles.toolbarRow}>
              <input
                type="text"
                placeholder="Search by name, username, email, or uid"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                style={styles.searchInput}
              />
              <button style={styles.primaryBtn} onClick={() => loadUsers(userQuery)}>Search</button>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={styles.avatar}>
                          {u.profile?.photoURL ? <img src={u.profile.photoURL} alt="" style={styles.avatarImg} /> : '👤'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{u.profile?.name || 'Unknown'}</div>
                          <div style={styles.mutedText}>{u.profile?.username ? `@${u.profile.username}` : u.id}</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>{u.profile?.email || '—'}</td>
                    <td style={styles.td}>{ROLE_LABELS[u.role] || u.role}</td>
                    <td style={styles.td}>
                      {u.banned ? 'Banned' : u.suspendedUntil ? `Suspended until ${formatDate(u.suspendedUntil)}` : 'Active'}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionsInline}>
                        {(() => {
                          const isSuper = myRole === 'super_admin';
                          const isMe = u.id === auth.currentUser?.uid;
                          const locked = isMe || u.role === 'super_admin' || (u.role === 'admin' && !isSuper) || myRole === 'moderator';
                          const options = isSuper ? ['user', 'moderator', 'admin', 'super_admin'] : ['user', 'moderator'];
                          return (
                            <select
                              value={u.role}
                              disabled={locked}
                              title={locked ? 'You cannot change this role' : 'Change role'}
                              onChange={(e) => handleUserRole(u.id, e.target.value)}
                              style={{ ...styles.select, padding: '4px 6px', fontSize: 12 }}
                            >
                              {Array.from(new Set([u.role, ...options])).map((r) => (
                                <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                              ))}
                            </select>
                          );
                        })()}
                        <button style={u.banned ? styles.smallBtn : styles.rejectBtn} onClick={() => handleUserBan(u.id, !u.banned)}>
                          {u.banned ? 'Unban' : 'Ban'}
                        </button>
                        {!u.banned && u.suspendedUntil && (
                          <button style={styles.smallBtn} onClick={() => handleUserBan(u.id, false)}>Unsuspend</button>
                        )}
                        {!u.banned && !u.suspendedUntil && u.role !== 'super_admin' && (
                          <button style={styles.smallBtn} onClick={() => handleUserSuspend(u.id)}>Suspend</button>
                        )}
                        {myRole === 'super_admin' && u.id !== auth.currentUser?.uid && u.role !== 'super_admin' && (
                          <button style={styles.rejectBtn} onClick={() => handleUserDelete(u.id, u.profile?.name || '')}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'ads' && (
          <div style={styles.tabContent}>
            <h2 style={styles.sectionTitle}>Ads Moderation</h2>
            <div style={styles.toolbarRow}>
              <select value={adFilter} onChange={(e) => setAdFilter(e.target.value as any)} style={styles.select}>
                <option value="pending_approval">Pending</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
                <option value="draft">Draft</option>
                <option value="all">All</option>
              </select>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Advertiser</th>
                  <th style={styles.th}>Budget</th>
                  <th style={styles.th}>Spent</th>
                  <th style={styles.th}>Impressions</th>
                  <th style={styles.th}>Clicks</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => (
                  <tr key={ad.id}>
                    <td style={styles.td}>{ad.title}</td>
                    <td style={styles.td}>{ad.createdBy || '—'}</td>
                    <td style={styles.td}>{formatMoney(ad.budget)}</td>
                    <td style={styles.td}>{formatMoney(ad.budgetSpent)}</td>
                    <td style={styles.td}>{ad.impressions || 0}</td>
                    <td style={styles.td}>{ad.clicks || 0}</td>
                    <td style={styles.td}>{ad.status}</td>
                    <td style={styles.td}>
                      <div style={styles.actionsInline}>
                        <button style={styles.smallBtn} onClick={() => handleAdUpdate(ad.id, 'active', 'approved')}>Approve</button>
                        <button style={styles.smallBtn} onClick={() => handleAdUpdate(ad.id, 'paused', 'paused_by_admin')}>Pause</button>
                        <button style={styles.rejectBtn} onClick={() => handleAdUpdate(ad.id, 'rejected', 'rejected_by_admin')}>Reject</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'verifications' && (
          <div style={styles.tabContent}>
            <h2 style={styles.sectionTitle}>Verifications</h2>
            <div style={styles.toolbarRow}>
              <select value={verificationFilter} onChange={(e) => setVerificationFilter(e.target.value as any)} style={styles.select}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="additional_info_required">Needs Info</option>
                <option value="all">All</option>
              </select>
            </div>
            {verifications.map((v) => (
              <div key={v.id} style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                      {v.fullName || v.userId}
                      {v.profile?.verified && <span title="Already verified" style={{ color: '#1D9BF0', marginLeft: 6 }}>✔</span>}
                    </div>
                    <div style={styles.mutedText}>
                      Type: <strong>{v.verificationType === 'public_figure' ? 'Public figure (blue tick)' : v.verificationType === 'artist' ? 'Music artist (royalties)' : 'Content creator'}</strong>
                    </div>
                    <div style={styles.mutedText}>
                      Profile:{' '}
                      <a href={`/profile/${encodeURIComponent(v.userId)}`} target="_blank" rel="noreferrer">
                        {v.profile?.name || v.userId}{v.profile?.username ? ` (@${v.profile.username})` : ''}
                      </a>
                    </div>
                    <div style={styles.mutedText}>Followers: {(v.followers ?? 0).toLocaleString()}</div>
                    <div style={styles.mutedText}>Status: {v.status}</div>
                    {v.createdAt && <div style={styles.mutedText}>Submitted: {new Date(v.createdAt).toLocaleString()}</div>}
                    {v.rejectionReason && <div style={styles.mutedText}>Reason: {v.rejectionReason}</div>}
                  </div>
                  <div>
                    <div style={styles.mutedText}>Documents ({v.documents?.length || 0}) — click to open:</div>
                    {v.documents?.map((doc, idx) => (
                      <div key={idx} style={{ fontSize: 13, marginTop: 4 }}>
                        {doc.viewUrl ? (
                          <a href={doc.viewUrl} target="_blank" rel="noreferrer">📄 {doc.type.replace(/_/g, ' ')}{doc.fileName ? ` — ${doc.fileName}` : ''}</a>
                        ) : (
                          <span>📄 {doc.type.replace(/_/g, ' ')} (unavailable)</span>
                        )}
                      </div>
                    ))}
                    {v.bankDetails?.bankName && (
                      <div style={{ ...styles.mutedText, marginTop: 8 }}>
                        Bank: {v.bankDetails.bankName} · {v.bankDetails.accountName} · {v.bankDetails.country}
                      </div>
                    )}
                  </div>
                </div>
                <div style={styles.actionsInline}>
                  <button style={styles.smallBtn} onClick={() => handleVerificationUpdate(v.id, 'approved')}>Approve</button>
                  <button style={styles.smallBtn} onClick={() => {
                    const reason = window.prompt('Rejection reason:') || '';
                    if (!reason.trim()) return;
                    handleVerificationUpdate(v.id, 'rejected', reason.trim());
                  }}>Reject</button>
                  <button style={styles.rejectBtn} onClick={() => {
                    const reason = window.prompt('What extra information is needed?') || '';
                    if (!reason.trim()) return;
                    handleVerificationUpdate(v.id, 'additional_info_required', reason.trim());
                  }}>Request Info</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'music' && (
          <div style={styles.tabContent}>
            <h2 style={styles.sectionTitle}>Music Library</h2>
            <div style={styles.toolbarRow}>
              <select value={musicFilter} onChange={(e) => setMusicFilter(e.target.value as any)} style={styles.select}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="all">All</option>
              </select>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Artist</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Public</th>
                  <th style={styles.th}>Reels</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tracks.map((track) => (
                  <tr key={track.id}>
                    <td style={styles.td}>{track.title}</td>
                    <td style={styles.td}>{track.artistName}</td>
                    <td style={styles.td}>{track.status}</td>
                    <td style={styles.td}>{track.isPublic ? 'Yes' : 'No'}</td>
                    <td style={styles.td}>{track.allowReels ? 'Yes' : 'No'}</td>
                    <td style={styles.td}>
                      <div style={styles.actionsInline}>
                        <button style={styles.smallBtn} onClick={() => handleTrackUpdate(track.id, { status: 'approved', isPublic: true, allowReels: true })}>Approve</button>
                        <button style={styles.smallBtn} onClick={() => handleTrackUpdate(track.id, { status: 'rejected', isPublic: false })}>Reject</button>
                        <button style={styles.rejectBtn} onClick={() => handleTrackDelete(track.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'reports' && (
          <div style={styles.tabContent}>
            <h2 style={styles.sectionTitle}>Reports ({reports.length})</h2>
            {reportNotice && (
              <div style={{ background: '#E8F7F2', color: '#0B4F51', padding: '10px 14px', borderRadius: 8, marginBottom: 14, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>✓ {reportNotice}</span>
                <button onClick={() => setReportNotice('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0B4F51' }}>✕</button>
              </div>
            )}
            {reports.length === 0 && <p style={{ color: '#666' }}>No pending reports. 🎉</p>}
            <div style={{ display: 'grid', gap: 14 }}>
              {reports.map((report) => {
                const pv = report.preview;
                const busy = busyReport === report.id;
                return (
                  <div key={report.id} style={{ border: '1px solid #E0E0E0', borderRadius: 12, padding: 14, background: '#fff', opacity: busy ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
                      <span style={{ background: '#072B2C', color: '#fff', borderRadius: 999, padding: '2px 10px', textTransform: 'capitalize' }}>{report.contentType || 'post'}</span>
                      <span style={{ background: '#FDECEA', color: '#B71C1C', borderRadius: 999, padding: '2px 10px' }}>{report.reason || 'Reported'}</span>
                      <span style={{ color: '#666' }}>{formatDate(report.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#444', marginBottom: 8 }}>
                      Posted by <strong>{pv?.author || report.reportedUserName || 'Unknown'}</strong>
                      {' · '}reported by <strong>{report.reporterName || 'a user'}</strong>
                      {report.additionalInfo ? <div style={{ marginTop: 4, fontStyle: 'italic' }}>“{report.additionalInfo}”</div> : null}
                    </div>
                    {pv ? (
                      <div style={{ background: '#F7F9F9', borderRadius: 10, padding: 10, marginBottom: 10 }}>
                        {pv.text ? <p style={{ margin: '0 0 8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 160, overflow: 'auto' }}>{pv.text}</p> : null}
                        {pv.videoURL ? (
                          <div style={{ maxWidth: 280 }}><MediaBox src={pv.videoURL} poster={pv.imageURL} type="video" controls contain maxHeight={320} /></div>
                        ) : pv.imageURL ? (
                          <div style={{ maxWidth: 280 }}><MediaBox src={pv.imageURL} type="image" contain maxHeight={320} /></div>
                        ) : null}
                      </div>
                    ) : (
                      <p style={{ color: '#999', fontSize: 13, margin: '0 0 10px' }}>This content no longer exists (already deleted).</p>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {pv?.link && (
                        <a href={pv.link} target="_blank" rel="noopener noreferrer" style={{ ...styles.smallBtn, background: '#E3F2FD', color: '#0D47A1', textDecoration: 'none' }}>👁️ Open on site</a>
                      )}
                      {pv && (
                        <button disabled={busy} style={styles.rejectBtn} onClick={() => handleDeleteReportContent(report.id)}>🗑️ Remove content</button>
                      )}
                      <button disabled={busy} style={styles.smallBtn} onClick={() => handleReportStatus(report.id, 'dismissed')} title="The content is fine; close the report">✅ Keep (dismiss report)</button>
                      {!pv && (
                        <button disabled={busy} style={styles.smallBtn} onClick={() => handleReportStatus(report.id, 'resolved')}>Close report</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {activeTab === 'withdrawals' && (
          <div style={styles.tabContent}>
            <h2 style={styles.sectionTitle}>Withdrawals ({withdrawals.length})</h2>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>PayPal email</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Requested</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((w) => {
                  const email: string = (w as any).paymentDetails?.email || '';
                  const st: string = (w as any).status || '';
                  const open = st === 'pending' || st === 'processing';
                  return (
                  <tr key={w.id}>
                    <td style={styles.td}>{w.userId || '—'}</td>
                    <td style={styles.td}><strong>{formatMoney(w.amount, w.currency || 'USD')}</strong></td>
                    <td style={styles.td}>
                      {email ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <strong style={{ wordBreak: 'break-all' }}>{email}</strong>
                          <button style={styles.smallBtn} onClick={() => { void navigator.clipboard?.writeText(email); }} title="Copy PayPal email">Copy</button>
                        </div>
                      ) : '—'}
                      {(w as any).failureReason ? <div style={styles.mutedText}>{(w as any).failureReason}</div> : null}
                    </td>
                    <td style={styles.td}>{st || '—'}</td>
                    <td style={styles.td}>{formatDate(w.createdAt)}</td>
                    <td style={styles.td}>
                      {open ? (
                      <div style={styles.actionsInline}>
                        <button style={styles.smallBtn} onClick={() => handleWithdrawalStatus(w.id, 'processing')} title="Send automatically via PayPal Payouts">Pay via PayPal</button>
                        <button style={styles.smallBtn} onClick={() => handleWithdrawalStatus(w.id, 'completed')} title="I sent the money manually from PayPal">Mark paid</button>
                        <button style={styles.rejectBtn} onClick={() => handleWithdrawalStatus(w.id, 'failed')}>Fail</button>
                      </div>
                      ) : '—'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'appeals' && (
          <div style={styles.tabContent}>
            <h2 style={styles.sectionTitle}>Appeals ({appeals.length})</h2>
            {appeals.length === 0 && <p style={{ color: '#666' }}>No pending appeals. 🎉</p>}
            <div style={{ display: 'grid', gap: 14 }}>
              {appeals.map((appeal) => {
                const pv = appeal.preview;
                const automatic = appeal.originalReason === 'automatic_uncertain_score';
                return (
                  <div key={appeal.id} style={{ border: '1px solid #E0E0E0', borderRadius: 12, padding: 14, background: '#fff' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
                      <span style={{ background: '#072B2C', color: '#fff', borderRadius: 999, padding: '2px 10px', textTransform: 'capitalize' }}>{appeal.contentType || 'content'}</span>
                      <span style={{ background: automatic ? '#FFF8E1' : '#E3F2FD', color: automatic ? '#8D6E00' : '#0D47A1', borderRadius: 999, padding: '2px 10px' }}>
                        {automatic ? 'Held for review' : `User appeal (${appeal.originalStatus || 'blocked'})`}
                      </span>
                      <span style={{ color: '#666' }}>{formatDate(appeal.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#444', marginBottom: 8 }}>
                      By <strong>{pv?.author || appeal.userName || 'Unknown user'}</strong>
                      {appeal.reason && !automatic ? <div style={{ marginTop: 4, fontStyle: 'italic', wordBreak: 'break-word' }}>“{appeal.reason}”</div> : null}
                    </div>
                    {pv ? (
                      <div style={{ background: '#F7F9F9', borderRadius: 10, padding: 10, marginBottom: 10 }}>
                        {pv.text ? <p style={{ margin: '0 0 8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 160, overflow: 'auto' }}>{pv.text}</p> : null}
                        {pv.videoURL ? (
                          <div style={{ maxWidth: 280 }}><MediaBox src={pv.videoURL} poster={pv.imageURL} type="video" controls contain maxHeight={320} /></div>
                        ) : pv.imageURL ? (
                          <div style={{ maxWidth: 280 }}><MediaBox src={pv.imageURL} type="image" contain maxHeight={320} /></div>
                        ) : null}
                      </div>
                    ) : (
                      <p style={{ color: '#999', fontSize: 13, margin: '0 0 10px' }}>This content no longer exists (deleted).</p>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <button style={styles.smallBtn} onClick={() => handleAppealResolve(appeal.id, 'approved')}>✅ Approve</button>
                      <button style={styles.rejectBtn} onClick={() => handleAppealResolve(appeal.id, 'rejected')}>Reject</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; prefix?: string; subLabel?: string; onClick?: () => void }> = ({ label, value, prefix, subLabel, onClick }) => (
  <div
    style={{ ...styles.statCard, ...(onClick ? { cursor: 'pointer' } : {}) }}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
  >
    <div style={styles.statValue}>{prefix ? `${prefix}${value.toLocaleString()}` : value.toLocaleString()}</div>
    <div style={styles.statLabel}>{label}</div>
    {subLabel && <div style={styles.statSub}>{subLabel}</div>}
  </div>
);

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5EDE1',
  },
  content: {
    paddingTop: 80,
    maxWidth: 1500,
    margin: '0 auto',
    padding: '20px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: 18,
  },
  error: {
    marginTop: 12,
    background: '#ffebee',
    color: '#b71c1c',
    padding: '8px 12px',
    borderRadius: 8,
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#072B2C',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: '#5B6E6E',
  },
  tabs: {
    display: 'flex',
    gap: 8,
    marginBottom: 32,
    borderBottom: '2px solid #ccc',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  tab: {
    padding: '12px 20px',
    background: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: '500',
    color: '#5B6E6E',
  },
  activeTab: {
    borderBottom: '3px solid #0B6B6F',
    color: '#0B6B6F',
  },
  refreshBtn: {
    marginLeft: 'auto',
    padding: '8px 14px',
    background: '#0B6B6F',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  tabContent: {
    minHeight: 400,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#072B2C',
    marginBottom: 20,
  },
  section: {
    marginBottom: 36,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 20,
    marginTop: 16,
  },
  statCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#0B6B6F',
  },
  statLabel: {
    fontSize: 14,
    color: '#5B6E6E',
    marginTop: 8,
  },
  statSub: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
  },
  toolbarRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  searchInput: {
    width: '100%',
    maxWidth: 420,
    padding: 12,
    borderRadius: 8,
    border: '1px solid #ccc',
    fontSize: 14,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
  },
  th: {
    background: '#0B6B6F',
    color: '#fff',
    padding: 12,
    textAlign: 'left',
    fontWeight: '600',
  },
  td: {
    padding: 12,
    borderBottom: '1px solid #eee',
    verticalAlign: 'top',
  },
  smallBtn: {
    padding: '6px 12px',
    background: '#66EAD1',
    color: '#072B2C',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
  },
  primaryBtn: {
    padding: '10px 16px',
    background: '#0B6B6F',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
  },
  rejectBtn: {
    padding: '6px 12px',
    background: '#f44336',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
  },
  select: {
    padding: 8,
    borderRadius: 6,
    border: '1px solid #ccc',
    fontSize: 14,
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  actionsInline: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  mutedText: {
    fontSize: 12,
    color: '#6b7280',
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#E6ECEC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
};

export default EnhancedAdminDashboard;






































